import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import JsonLd from "@/components/base/JsonLd";
import { getOrganizationSchema, getBreadcrumbSchema } from "@/components/base/SchemaOrg";
import { aboutInfo, testimonials } from "@/mocks/home";
import type { BreadcrumbItem } from "@/components/base/SchemaOrg";

const siteUrl = "https://www.91madou.com";

const breadcrumbs: BreadcrumbItem[] = [
  { name: "首页", url: siteUrl },
  { name: "关于我们", url: `${siteUrl}/about` },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    setFormError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("phone_alt") as string || "").trim();
    if (honeypot) {
      setFormStatus("success");
      form.reset();
      return;
    }
    formData.delete("phone_alt");

    try {
      const response = await fetch("https://readdy.ai/api/form/d9nh7h68mbnljin5a0lg", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && (parsed as { code?: string }).code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const serverMsg =
          (parsed as { meta?: { message?: string } }).meta?.message ||
          (parsed as { message?: string }).message ||
          (parsed as { meta?: { detail?: string } }).meta?.detail ||
          responseText;
        if (typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam")) {
          setFormError("提交失败，请稍后重试");
        } else {
          setFormError(typeof serverMsg === "string" ? serverMsg : "提交失败，请稍后重试");
        }
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查网络连接后重试");
      setFormStatus("error");
    }
  };

  return (
    <>
      <JsonLd data={getOrganizationSchema()} />
      <JsonLd data={getBreadcrumbSchema(breadcrumbs)} />
      <ScrollToTop />
      <Navbar />
      <main>
        <section className="pt-20 pb-8 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800 font-medium">关于我们</span>
              </nav>
              <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mb-2">
                关于{aboutInfo.brandName}
              </h1>
              <p className="text-foreground-600 text-sm md:text-base max-w-3xl">
                了解我们的品牌故事、发展历程与核心价值
              </p>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 items-start">
              <div className="lg:w-1/2">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-4">
                  品牌介绍
                </h2>
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base mb-4">
                  {aboutInfo.description}
                </p>
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base mb-6">
                  自{aboutInfo.founded}成立以来，{aboutInfo.brandName}始终秉持&ldquo;{aboutInfo.mission}&rdquo;的初心，不断丰富内容资源，优化用户体验。目前平台拥有注册用户{aboutInfo.users}，收录影视内容{aboutInfo.contentCount}部，每日更新{aboutInfo.dailyUpdate}部以上，已成为国内最受欢迎的在线视频平台之一。
                </p>
              </div>
              <div className="lg:w-1/2 grid grid-cols-2 gap-4 w-full">
                <div className="p-5 rounded-xl bg-background-100 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary-100 flex items-center justify-center">
                    <i className="ri-user-star-line text-2xl text-primary-500"></i>
                  </div>
                  <div className="font-heading font-bold text-2xl text-primary-500 mb-1">{aboutInfo.users}</div>
                  <div className="text-xs text-foreground-500">注册用户</div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-accent-100 flex items-center justify-center">
                    <i className="ri-film-line text-2xl text-accent-500"></i>
                  </div>
                  <div className="font-heading font-bold text-2xl text-accent-500 mb-1">{aboutInfo.contentCount}</div>
                  <div className="text-xs text-foreground-500">影视内容</div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-secondary-100 flex items-center justify-center">
                    <i className="ri-calendar-check-line text-2xl text-secondary-500"></i>
                  </div>
                  <div className="font-heading font-bold text-2xl text-secondary-500 mb-1">7x24</div>
                  <div className="text-xs text-foreground-500">服务时间</div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary-100 flex items-center justify-center">
                    <i className="ri-thumb-up-line text-2xl text-primary-500"></i>
                  </div>
                  <div className="font-heading font-bold text-2xl text-primary-500 mb-1">98%</div>
                  <div className="text-xs text-foreground-500">用户满意度</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-2">
                用户口碑
              </h2>
              <p className="text-foreground-600 text-sm">听听用户们怎么说</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="p-6 rounded-xl bg-background-50">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`ri-star-fill text-sm ${
                          i < t.rating ? "text-accent-500" : "text-background-300"
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-foreground-700 text-sm leading-relaxed mb-5">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <span className="font-medium text-sm text-foreground-900">{t.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="lg:w-1/2">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-4">
                  联系方式
                </h2>
                <p className="text-foreground-600 text-sm mb-6">
                  如有任何问题或建议，欢迎通过以下方式联系我们
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900 mb-0.5">电子邮箱</div>
                      <span className="text-sm text-foreground-600">{aboutInfo.contactEmail}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-accent-500 text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900 mb-0.5">客服电话</div>
                      <span className="text-sm text-foreground-600">{aboutInfo.contactPhone}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-secondary-500 text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900 mb-0.5">公司地址</div>
                      <span className="text-sm text-foreground-600">{aboutInfo.address}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:w-1/2">
                <div className="bg-background-100 rounded-xl p-6">
                  <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-4">
                    给我们留言
                  </h3>
                  <form className="space-y-4" data-readdy-form="" onSubmit={handleSubmit}>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        name="name"
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        name="email"
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea
                        name="message"
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors duration-200 resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      className="honeypot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    {formStatus === "success" && (
                      <div className="px-4 py-3 rounded-lg bg-green-50 text-green-700 text-sm">
                        提交成功！感谢您的留言，我们会尽快回复。
                      </div>
                    )}
                    {formStatus === "error" && (
                      <div className="px-4 py-3 rounded-lg bg-red-50 text-red-700 text-sm">
                        {formError}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={formStatus === "submitting"}
                      className="w-full px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {formStatus === "submitting" ? "提交中..." : "提交留言"}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}